import { useMemo, useState } from 'react';
import { Navigate, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authFetch } from '../features/auth/auth-fetch.js';
import { useAuth } from '../features/auth/use-auth.js';
import { HOTELS_QUERY_KEY } from '../features/hotels/api.js';
import { Card } from '../components/ui/card.js';
import { Button } from '../components/ui/button.js';
import { useConfirm } from '../components/ui/confirm-dialog.js';

interface JobLogRow {
  id: string;
  queue: string;
  jobId: string;
  jobName: string;
  state: 'queued' | 'active' | 'completed' | 'failed' | 'stalled';
  organizationId: string | null;
  userId: string | null;
  userEmail: string | null;
  hotelId: string | null;
  hotelName: string | null;
  details: Record<string, unknown>;
  attempts: number;
  durationMs: number | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
}

interface JobsListResponse {
  active: JobLogRow[];
  recent: JobLogRow[];
}

const QUEUE_LABEL: Record<string, string> = {
  'hotel-intake': 'Weboldal beolvasás',
  'topic-ideation': 'AI témajavaslat',
  'copy-generation': 'AI tartalom-generálás',
  'email-dispatch': 'Email küldés',
};

/**
 * Human-readable function description that goes beyond the queue name —
 * combines queue + jobName + payload hints (per-post mode, email type, etc.)
 * into a single short sentence the user actually understands.
 */
function humanizeFunction(job: JobLogRow): string {
  const d = job.details ?? {};
  if (job.queue === 'hotel-intake') {
    return job.jobName === 'crawl-and-extract'
      ? 'Hotel weboldal letöltése + Brand Voice / USP kinyerése'
      : `Hotel intake: ${job.jobName}`;
  }
  if (job.queue === 'topic-ideation') {
    const perPost = (d as { perPost?: boolean }).perPost;
    return perPost
      ? 'AI témajavaslat — egy konkrét slotra (variáció)'
      : 'AI témajavaslat — havi terv üres slotjaira';
  }
  if (job.queue === 'copy-generation') {
    return 'AI tartalom-generálás — FB + IG poszt szöveg írása';
  }
  if (job.queue === 'email-dispatch') {
    const kind = (d as { kind?: string }).kind ?? job.jobName;
    if (kind === 'magic-link' || job.jobName === 'magic-link')
      return 'Bejelentkezési link kiküldése';
    if (kind === 'approval-link' || job.jobName === 'approval-link')
      return 'Jóváhagyási link kiküldése ügyfélnek';
    return `Email küldés: ${kind ?? job.jobName}`;
  }
  return `${job.queue} / ${job.jobName}`;
}

/**
 * Translate the most common technical error messages we emit into a
 * one-sentence Hungarian summary an admin can act on. Unknown errors
 * pass through unchanged so we never hide information.
 */
function humanizeError(message: string, queue: string): string {
  const m = message.toLowerCase();
  if (m.includes('zod validation') || m.includes('failed zod')) {
    if (queue === 'hotel-intake') {
      return 'Az AI nem a vártnak megfelelő formátumban adta vissza az adatokat — Brand Voice / USP mezők nem írhatók be. Próbáld újra, ha többször ismétlődik, érdemes másik modellt választani az AI beállításokban (Weboldal beolvasás slot).';
    }
    if (queue === 'topic-ideation') {
      return 'Az AI nem érvényes témajavaslatot adott vissza (hiányos vagy hibás mezők). Próbáld újra, vagy válts erősebb modellre a HEAVY slot-ban.';
    }
    if (queue === 'copy-generation') {
      return 'Az AI nem érvényes poszt-szöveget adott vissza. Próbáld újra, ha többször előfordul, válts modellt a ROUTINE slot-ban.';
    }
    return 'Az AI válasza nem felelt meg az elvárt formátumnak.';
  }
  if (m.includes('429') || m.includes('rate limit') || m.includes('too many requests')) {
    return 'Az AI szolgáltató elérte a rate limitet — túl sok hívás rövid idő alatt. Várj 1-2 percet és próbáld újra.';
  }
  if (m.includes('insufficient') && (m.includes('credit') || m.includes('balance'))) {
    return 'Az OpenRouter számlán elfogyott a credit / hitel. Töltsd fel az API key fiókját.';
  }
  if (m.includes('401') || m.includes('unauthorized') || m.includes('invalid api key')) {
    return 'Az AI provider elutasította a hitelesítést — valószínűleg lejárt vagy hibás az API kulcs.';
  }
  if (m.includes('timeout') || m.includes('timed out') || m.includes('aborterror')) {
    return queue === 'hotel-intake'
      ? 'A weboldal lassan válaszolt vagy az AI hívás időtúllépésbe futott. Próbáld újra; ha ismétlődik, ellenőrizd hogy a weboldal elérhető-e.'
      : 'A művelet időtúllépésbe futott. Próbáld újra.';
  }
  if (m.includes('econnrefused') || m.includes('enotfound') || m.includes('network')) {
    return 'Hálózati hiba: nem sikerült elérni egy külső szolgáltatást. Lehet, hogy a Redis, Postgres vagy az AI provider nem elérhető.';
  }
  if (m.includes('not found') && queue === 'hotel-intake') {
    return 'A megadott hotel nem található az adatbázisban — valószínűleg törölve lett a job indítása után.';
  }
  // Fallback: return the raw message — it's still useful to admins.
  return message;
}

const STATE_TONE: Record<JobLogRow['state'], { bg: string; fg: string; label: string }> = {
  queued: { bg: 'var(--st-neutral-bg)', fg: 'var(--st-neutral-fg)', label: 'Várólistán' },
  active: {
    bg: 'var(--st-topic-pending-bg)',
    fg: 'var(--st-topic-pending-fg)',
    label: 'Folyamatban',
  },
  completed: { bg: 'var(--brand-50)', fg: 'var(--brand-700)', label: 'Sikeres' },
  failed: { bg: 'var(--st-rejected-bg)', fg: 'var(--danger)', label: 'Hiba' },
  stalled: { bg: 'var(--st-topic-pending-bg)', fg: 'var(--st-topic-pending-fg)', label: 'Elakadt' },
};

async function fetchJobs(params: {
  queue?: string | undefined;
  state?: string | undefined;
}): Promise<JobsListResponse> {
  const qs = new URLSearchParams();
  if (params.queue) qs.set('queue', params.queue);
  if (params.state) qs.set('state', params.state);
  const r = await authFetch(`/admin/jobs${qs.toString() ? `?${qs}` : ''}`);
  if (!r.ok) throw new Error('failed to load jobs');
  return r.json();
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('hu-HU');
  } catch {
    return iso;
  }
}

function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} perce`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} órája`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'tegnap';
  return `${d} napja`;
}

function formatDuration(ms: number | null): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const m = Math.floor(ms / 60_000);
  const s = Math.floor((ms % 60_000) / 1000);
  return `${m}m ${s}s`;
}

function JobRow({
  job,
  defaultOpen,
  onCancel,
  canceling,
}: {
  job: JobLogRow;
  defaultOpen?: boolean;
  onCancel?: () => void;
  canceling?: boolean;
}) {
  const [open, setOpen] = useState(!!defaultOpen);
  const confirm = useConfirm();
  const tone = STATE_TONE[job.state] ?? STATE_TONE.completed;
  const error = (job.details.error ?? null) as {
    message?: string;
    name?: string;
    stack?: string | null;
  } | null;

  const friendly = humanizeFunction(job);
  const errorSummary = error?.message ? humanizeError(error.message, job.queue) : null;

  return (
    <li className="border-b border-[var(--app-divider)] last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-[var(--app-hover)]"
      >
        <span
          className="mt-0.5 inline-flex h-6 shrink-0 items-center gap-1.5 rounded-full px-2.5 text-meta font-semibold uppercase tracking-wider"
          style={{ background: tone.bg, color: tone.fg }}
        >
          {tone.label}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
            <span className="text-body font-semibold text-[var(--ink-700)]">{friendly}</span>
            {job.hotelName && (
              <span className="inline-flex items-center gap-1 text-meta text-[var(--brand-700)]">
                <span className="text-[var(--fg-faint)]">·</span>
                {job.hotelName}
              </span>
            )}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-meta text-[var(--fg-faint)]">
            <span>{formatRelative(job.startedAt ?? job.createdAt)}</span>
            <span>·</span>
            <span>{formatDuration(job.durationMs)}</span>
            <span>·</span>
            <span>
              {job.userEmail ?? <span className="italic text-[var(--fg-faint)]">rendszer</span>}
            </span>
          </div>
        </div>
        <span
          className={`mt-1 text-[var(--fg-faint)] transition-transform ${open ? 'rotate-90' : ''}`}
          aria-hidden
        >
          ›
        </span>
      </button>
      {open && (
        <div className="space-y-3 bg-[var(--app-bg)] px-4 py-3 text-meta text-[var(--ink-700)]">
          {/* Plain-Hungarian summary box — first, biggest, easiest to read. */}
          {errorSummary && (
            <div className="rounded-md border border-[var(--st-rejected-bg)] bg-[var(--st-rejected-bg)] p-3">
              <div className="text-meta font-semibold uppercase tracking-wider text-[var(--danger)]">
                Mi történt
              </div>
              <p className="mt-1 text-body leading-snug text-[var(--danger)]">{errorSummary}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
            <Field label="Funkció" value={friendly} />
            <Field
              label="Folyamat típusa"
              value={QUEUE_LABEL[job.queue] ?? <span className="font-mono">{job.queue}</span>}
            />
            <Field
              label="Hotel"
              value={
                job.hotelName ? (
                  <span className="font-medium text-[var(--brand-700)]">{job.hotelName}</span>
                ) : job.hotelId ? (
                  <span className="font-mono text-[var(--fg-faint)]">
                    {job.hotelId.slice(0, 8)}… (törölt)
                  </span>
                ) : (
                  '—'
                )
              }
            />
            <Field label="Felhasználó" value={job.userEmail ?? '— rendszer —'} />
            <Field label="Indult" value={formatDateTime(job.startedAt ?? job.createdAt)} />
            <Field label="Befejezve" value={formatDateTime(job.finishedAt)} />
            <Field label="Próbálkozások" value={String(job.attempts)} />
            <Field
              label="Folyamat azonosító"
              value={<span className="font-mono text-meta">{job.jobId}</span>}
            />
          </div>
          {onCancel && job.state === 'active' && (
            <div className="flex items-center justify-between rounded-md border border-[var(--st-rejected-bg)] bg-[var(--st-rejected-bg)] p-3">
              <div className="text-body text-[var(--danger)]">
                A folyamat még fut. Megállíthatod, ha úgy ítéled hogy beragadt vagy nem akarod
                megvárni.
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={async (e) => {
                  e.stopPropagation();
                  const ok = await confirm({
                    title: 'Folyamat megállítása',
                    message: 'Biztosan megállítod ezt a folyamatot?',
                    confirmLabel: 'Megállítás',
                    tone: 'destructive',
                  });
                  if (ok) onCancel();
                }}
                disabled={canceling}
                className="text-[var(--danger)]"
              >
                {canceling ? 'Megállítás…' : 'Folyamat megállítása'}
              </Button>
            </div>
          )}
          {error?.message && (
            <details className="rounded-md border border-[var(--app-divider)] bg-[var(--app-surface)] p-3">
              <summary className="cursor-pointer text-meta font-semibold text-[var(--fg-2)]">
                Részletes hibainformáció (fejlesztőknek)
              </summary>
              <div className="mt-2 text-meta font-semibold text-[var(--danger)]">
                {error.name ?? 'Error'}: {error.message}
              </div>
              {error.stack && (
                <pre className="mt-2 max-h-[260px] overflow-auto whitespace-pre-wrap text-meta text-[var(--danger)]">
                  {error.stack}
                </pre>
              )}
            </details>
          )}
          <details className="rounded-md border border-[var(--app-divider)] bg-[var(--app-surface)] p-3">
            <summary className="cursor-pointer text-meta font-semibold text-[var(--fg-2)]">
              Nyers payload (JSON)
            </summary>
            <pre className="mt-2 max-h-[260px] overflow-auto whitespace-pre-wrap text-meta text-[var(--ink-700)]">
              {JSON.stringify(job.details, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </li>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-2">
      <span className="w-[110px] shrink-0 text-meta uppercase tracking-wider text-[var(--fg-faint)]">
        {label}
      </span>
      <span className="min-w-0 break-words text-meta">{value}</span>
    </div>
  );
}

function JobsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [queueFilter, setQueueFilter] = useState<string>('');
  const [stateFilter, setStateFilter] = useState<string>('');

  const q = useQuery<JobsListResponse>({
    queryKey: ['admin-jobs', queueFilter, stateFilter],
    queryFn: () => fetchJobs({ queue: queueFilter || undefined, state: stateFilter || undefined }),
    // Refresh every 5s so admins watching a running crawl see progress.
    refetchInterval: 5_000,
  });

  const cancel = useMutation({
    mutationFn: async (vars: { queue: string; jobId: string }) => {
      const r = await authFetch(`/admin/jobs/cancel/${vars.queue}/${vars.jobId}`, {
        method: 'POST',
      });
      if (!r.ok) throw new Error('A megállítás nem sikerült.');
      return r.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-jobs'] });
      void qc.invalidateQueries({ queryKey: HOTELS_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: ['intake-status'] });
    },
  });

  const counts = useMemo(() => {
    if (!q.data) return { active: 0, running: 0, queued: 0, failed: 0, completed: 0 };
    const running = q.data.active.filter((r) => r.state === 'active').length;
    return {
      active: q.data.active.length,
      running,
      queued: q.data.active.length - running,
      failed: q.data.recent.filter((r) => r.state === 'failed').length,
      completed: q.data.recent.filter((r) => r.state === 'completed').length,
    };
  }, [q.data]);

  if (user && user.role !== 'admin') {
    return <Navigate to="/app" />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="h2-eyebrow">Rendszer</div>
          <h1 className="mt-1.5 font-sans text-h1 font-normal leading-none tracking-[-0.02em] text-[var(--ink-700)]">
            Folyamatok
          </h1>
          <p className="mt-2 max-w-[60ch] text-body text-[var(--fg-2)]">
            Élőben látható, hogy melyik AI / crawl / email job fut éppen, és minden befejezett vagy
            elakadt folyamat naplója visszakövethető. A bejegyzések tartalmazzák a kezdeményező
            felhasználót, a futási időt és — hiba esetén — a teljes stack trace-t.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Chip value={counts.active} label="aktív" tone="warn" />
          <Chip value={counts.completed} label="sikeres" tone="done" />
          <Chip value={counts.failed} label="hiba" tone="danger" />
        </div>
      </div>

      {/* Active jobs panel — always visible at top so you immediately see
          if a crawl is hanging or piling up. */}
      <Card className="p-0">
        <header className="flex items-center justify-between border-b border-[var(--app-divider)] px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-[var(--brand-500)]" />
            <h2 className="font-sans text-h2 font-semibold text-[var(--ink-700)]">
              Aktív folyamatok
            </h2>
            <span className="text-meta text-[var(--fg-faint)]">
              {counts.running} fut{counts.queued > 0 ? ` · ${counts.queued} várólistán` : ''}
            </span>
          </div>
          {q.isFetching && <span className="text-meta text-[var(--fg-faint)]">frissítés…</span>}
        </header>
        {q.isLoading ? (
          <p className="px-4 py-6 text-center text-body text-[var(--fg-2)]">Betöltés…</p>
        ) : !q.data || q.data.active.length === 0 ? (
          <p className="px-4 py-6 text-center text-body italic text-[var(--fg-faint)]">
            Nincs aktív folyamat.
          </p>
        ) : (
          <ul>
            {q.data.active.map((j) => (
              <JobRow
                key={j.id}
                job={j}
                defaultOpen
                onCancel={() => cancel.mutate({ queue: j.queue, jobId: j.jobId })}
                canceling={
                  cancel.isPending &&
                  cancel.variables?.queue === j.queue &&
                  cancel.variables?.jobId === j.jobId
                }
              />
            ))}
          </ul>
        )}
      </Card>

      {/* History */}
      <Card className="p-0">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--app-divider)] px-4 py-3">
          <div className="flex items-center gap-3">
            <h2 className="font-sans text-h2 font-semibold text-[var(--ink-700)]">Előzmények</h2>
            <select
              value={queueFilter}
              onChange={(e) => setQueueFilter(e.target.value)}
              className="h-7 rounded-full border border-[var(--app-divider)] bg-[var(--app-surface)] px-3 text-meta"
            >
              <option value="">Minden típus</option>
              <option value="hotel-intake">Weboldal beolvasás</option>
              <option value="topic-ideation">AI témajavaslat</option>
              <option value="copy-generation">AI tartalom-generálás</option>
              <option value="email-dispatch">Email küldés</option>
            </select>
            <select
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
              className="h-7 rounded-full border border-[var(--app-divider)] bg-[var(--app-surface)] px-3 text-meta"
            >
              <option value="">Minden állapot</option>
              <option value="completed">Sikeres</option>
              <option value="failed">Hibás</option>
            </select>
          </div>
        </header>
        {q.isLoading ? (
          <p className="px-4 py-6 text-center text-body text-[var(--fg-2)]">Betöltés…</p>
        ) : !q.data || q.data.recent.length === 0 ? (
          <p className="px-4 py-6 text-center text-body italic text-[var(--fg-faint)]">
            Nincs előzmény.
          </p>
        ) : (
          <ul>
            {q.data.recent.map((j) => (
              <JobRow key={j.id} job={j} />
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function Chip({
  value,
  label,
  tone,
}: {
  value: number;
  label: string;
  tone: 'warn' | 'done' | 'danger';
}) {
  const tones: Record<string, string> = {
    warn: 'bg-[var(--st-topic-pending-bg)] text-[var(--st-topic-pending-fg)] border-[var(--st-topic-pending-bg)]',
    done: 'bg-[var(--brand-50)] text-[var(--brand-700)] border-[var(--brand-50)]',
    danger: 'bg-[var(--st-rejected-bg)] text-[var(--danger)] border-[var(--st-rejected-bg)]',
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-meta font-medium ${tones[tone]}`}
    >
      <span className="font-mono font-semibold">{value}</span>
      <span className="text-meta uppercase tracking-wider opacity-80">{label}</span>
    </span>
  );
}

export const Route = createFileRoute('/app/admin/jobs')({
  component: JobsPage,
});
